import { RootPath } from "@/models/route"

const SIGN_IN_PATH = `/${RootPath.SignIn}`

/**
 * Builds the sign-in URL with an optional `from` query param for post-login redirect.
 *
 * Omits `from` when the attempted path is `/` (root).
 */
export const buildSignInRedirectUrl = (
  pathname: string,
  search = ""
): string => {
  const normalizedSearch = search.length > 1 ? search : ""
  const currentUrl = `${pathname}${normalizedSearch}`
  const searchParams = new URLSearchParams(
    currentUrl === RootPath.Root ? {} : { from: currentUrl }
  )
  const query = searchParams.size ? `?${searchParams.toString()}` : ""

  return `${SIGN_IN_PATH}${query}`
}

const SIGN_IN_RETURN_PATH_BASE = "https://nzero.invalid"

const isSafeApplicationPath = (value: string): boolean =>
  value.startsWith("/") &&
  !value.startsWith("//") &&
  !value.includes("\\") &&
  !value.includes("://")

const isSignInLoopPath = (pathname: string): boolean =>
  pathname === SIGN_IN_PATH || pathname.startsWith(`${SIGN_IN_PATH}/`)

/**
 * Accepts only same-origin application paths for post-login navigation.
 * External, protocol-relative, malformed, and sign-in-loop values fall back to `/`.
 */
export const resolveSafeSignInReturnPath = (
  from: string | null | undefined
): string => {
  if (!from || !isSafeApplicationPath(from)) {
    return RootPath.Root
  }

  try {
    const url = new URL(from, SIGN_IN_RETURN_PATH_BASE)
    const resolvedPath = `${url.pathname}${url.search}`
    const decodedPathname = decodeURIComponent(url.pathname)

    if (
      url.origin !== SIGN_IN_RETURN_PATH_BASE ||
      !isSafeApplicationPath(resolvedPath) ||
      !isSafeApplicationPath(decodedPathname) ||
      isSignInLoopPath(url.pathname) ||
      isSignInLoopPath(decodedPathname)
    ) {
      return RootPath.Root
    }

    return resolvedPath
  } catch {
    return RootPath.Root
  }
}

let isRedirectingToSignIn = false

export const redirectToSignIn = (): void => {
  if (isRedirectingToSignIn) {
    return
  }
  if (globalThis.location.pathname === `/${RootPath.SignIn}`) {
    return
  }

  isRedirectingToSignIn = true
  globalThis.setTimeout(() => {
    isRedirectingToSignIn = false
  }, 5000)

  globalThis.location.replace(
    buildSignInRedirectUrl(
      globalThis.location.pathname,
      globalThis.location.search
    )
  )
}
